/**
 * ====================================================================
 * FIKO CAD COORDINATE DIMENSION
 * ====================================================================
 *
 * Koordinat ölçüsü - Tek nokta için X, Y, Z koordinatlarını gösterir.
 * CATIA'nın koordinat okuma özelliğine karşılık gelir.
 *
 * Özellikler:
 * - Çoklu satır görüntüleme (X, Y, Z)
 * - Referans frame desteği (lokal koordinat sistemi)
 * - Sabit boyut (kamera zoom'undan bağımsız)
 * - 3D leader line (nokta → etiket)
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 * @license Commercial - Satılabilir Kalite
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    GeometryReference,
    BaseDimensionOptions,
    ArrowStyle,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

// ====================================================================
// KOORDINAT FRAME INTERFACE
// ====================================================================

/**
 * Koordinat referans çerçevesi
 * CATIA'daki gibi farklı koordinat sistemleri desteği
 */
export interface CoordinateFrame {
    /** Orijin noktası (world space) */
    origin: THREE.Vector3;
    /** Rotasyon quaternion */
    quaternion: THREE.Quaternion;
    /** Frame adı (opsiyonel) */
    name?: string;
}

/**
 * Coordinate dimension için özel seçenekler
 */
export interface CoordinateDimensionOptions extends BaseDimensionOptions {
    /** Referans frame (lokal koordinat sistemi için) */
    referenceFrame?: CoordinateFrame;
    /** Leader line göster */
    showLeaderLine?: boolean;
    /** Leader line uzunluğu */
    leaderLength?: number;
    /** Leader line açısı (derece) */
    leaderAngle?: number;
    /** Her eksen için renk */
    axisColors?: {
        x?: string;
        y?: string;
        z?: string;
    };
    /** Koordinat formatı */
    format?: 'xyz' | 'compact' | 'full';
}

// ====================================================================
// COORDINATE DIMENSION CLASS
// ====================================================================

/**
 * Coordinate Dimension - Koordinat ölçüsü
 * Tek nokta seçildiğinde X, Y, Z değerlerini gösterir
 */
export class CoordinateDimension extends Dimension {
    // Referans
    private ref: GeometryReference;
    private referenceFrame: CoordinateFrame | null = null;

    // Hesaplanmış değerler
    private worldPoint: THREE.Vector3 = new THREE.Vector3();
    private localPoint: THREE.Vector3 = new THREE.Vector3();

    // Çizim elemanları
    private leaderLine: LineSegments2 | null = null;
    private pointMarker: THREE.Mesh | null = null;
    private label: THREE.Sprite | null = null;

    // Shared geometries
    private static _sharedPointGeometry: THREE.BufferGeometry | null = null;

    // Sahne referansı
    private scene: FikoSahne;

    // Pre-allocated vectors (zero-allocation pattern)
    private readonly _tempVec3 = new THREE.Vector3();
    private readonly _tempQuat = new THREE.Quaternion();

    // 🔴 SABİT BOYUT SİSTEMİ - FikoOlcumCizgisi ile aynı pattern
    sabitBoyut = true;
    private hazirlaListenerEklendi: boolean = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // 🚀 PERFORMANS: Smooth scale interpolation için
    private lastKameraUpdateTime: number = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS: number = 100;
    private targetScaleAdjustment: number = 1;
    private currentScaleAdjustment: number = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    constructor(
        ref: GeometryReference,
        options: CoordinateDimensionOptions,
        scene: FikoSahne
    ) {
        super(DimensionType.ORDINATE, options);

        this.ref = ref;
        this.scene = scene;

        if (options.referenceFrame) {
            this.referenceFrame = options.referenceFrame;
        }
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Nokta al
            if (!this.ref.point) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Nokta tanımlı değil'
                    }
                };
            }

            this.worldPoint.copy(this.ref.point);

            // Lokal koordinata dönüştür
            this.localPoint.copy(this.transformToLocalFrame(this.worldPoint));

            // Değeri hesapla (magnitude)
            this._value = this.calculateValue();

            // Validate
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Geçersiz koordinat'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // Sahneye hazırla (event listener bağla)
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Coordinate dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Dimension'ı güncelle
     */
    update(): void {
        // Scale güncelleme event listener üzerinden yapılır
    }

    /**
     * ✅ Sahneye hazırlama - FikoOlcumCizgisi ile aynı pattern
     */
    hazirla(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }

        try {
            if (this.label && this.label.userData.originalScale) {
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Hata durumunda sessizce devam et
        }
    }

    /**
     * 🚀 PERFORMANS: Throttled kamera event handler
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        if (this.kameraUpdateTimeout !== null) {
            return;
        }

        if (now - this.lastKameraUpdateTime < CoordinateDimension.KAMERA_THROTTLE_MS) {
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, CoordinateDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * 🔴 YAZININ BOYUTU SABİT - Çizgi normal kalır
     */
    private kameraYonDegisti(): void {
        if (!this.scene || !this.scene.anaGosterim || !this.scene.anaGosterim.aktifKamera) {
            return;
        }

        if (this.sabitBoyut && this.label && this.label.userData.originalScale) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;

            this.targetScaleAdjustment = factor * 0.02;
            this.startSmoothInterpolation();
        }
    }

    /**
     * 🎯 SMOOTH: Her frame interpolation
     */
    private startSmoothInterpolation(): void {
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (!this.label || !this.label.userData.originalScale || !this.sabitBoyut) {
                this.interpolationRAF = null;
                return;
            }

            const lerpFactor = 0.2;
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            this.currentScaleAdjustment += diff * lerpFactor;

            const originalScale = this.label.userData.originalScale as THREE.Vector3;
            this.label.scale.set(
                originalScale.x * this.currentScaleAdjustment,
                originalScale.y * this.currentScaleAdjustment,
                1
            );

            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * Validate
     */
    validate(): boolean {
        return !isNaN(this.localPoint.x) &&
               !isNaN(this.localPoint.y) &&
               !isNaN(this.localPoint.z);
    }

    /**
     * Değeri hesapla (magnitude - informational)
     */
    protected calculateValue(): number {
        return this.localPoint.length();
    }

    /**
     * Referans frame'e göre dönüşüm
     * CATIA'daki gibi farklı koordinat sistemleri desteği
     */
    private transformToLocalFrame(worldPoint: THREE.Vector3): THREE.Vector3 {
        if (!this.referenceFrame) {
            return worldPoint.clone();
        }

        // Pre-allocated vector kullan
        this._tempVec3.copy(worldPoint);
        this._tempVec3.sub(this.referenceFrame.origin);

        // Ters quaternion uygula
        this._tempQuat.copy(this.referenceFrame.quaternion).invert();
        this._tempVec3.applyQuaternion(this._tempQuat);

        return this._tempVec3.clone();
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        this.clearGeometry();

        const options = this._options as CoordinateDimensionOptions;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const lineWidth = options.lineWidth || DIMENSION_CONSTANTS.DEFAULT_LINE_WIDTH;
        const showLeader = options.showLeaderLine !== false;
        const leaderLength = options.leaderLength || 5;
        const leaderAngle = (options.leaderAngle || 45) * Math.PI / 180;

        // Nokta işaretleyici
        this.pointMarker = this.createPointMarker(this.worldPoint, color);
        this.pointMarker.name = 'PointMarker';
        this.add(this.pointMarker);

        // Leader line hesapla
        const leaderDir = new THREE.Vector3(
            Math.cos(leaderAngle),
            Math.sin(leaderAngle),
            0
        );

        // Kameraya göre ayarla
        if (this.scene?.anaGosterim?.aktifKamera) {
            const camera = this.scene.anaGosterim.aktifKamera;
            const cameraDir = new THREE.Vector3()
                .subVectors(camera.position, this.worldPoint)
                .normalize();

            // Leader kameraya dik düzlemde olsun
            const right = new THREE.Vector3()
                .crossVectors(cameraDir, new THREE.Vector3(0, 1, 0))
                .normalize();
            const up = new THREE.Vector3()
                .crossVectors(right, cameraDir)
                .normalize();

            leaderDir.set(
                right.x * Math.cos(leaderAngle) + up.x * Math.sin(leaderAngle),
                right.y * Math.cos(leaderAngle) + up.y * Math.sin(leaderAngle),
                right.z * Math.cos(leaderAngle) + up.z * Math.sin(leaderAngle)
            );
        }

        const labelPos = this.worldPoint.clone()
            .addScaledVector(leaderDir, leaderLength);

        // Leader line
        if (showLeader) {
            this.leaderLine = this.createLine(
                [
                    this.worldPoint.x, this.worldPoint.y, this.worldPoint.z,
                    labelPos.x, labelPos.y, labelPos.z
                ],
                color,
                lineWidth
            );
            this.leaderLine.name = 'LeaderLine';
            this.add(this.leaderLine);
        }

        // Label (çoklu satır)
        this.label = this.createMultiLineLabel(labelPos, options);
        this.label.name = 'CoordinateLabel';
        this.add(this.label);
    }

    /**
     * Nokta işaretleyici oluştur
     */
    private createPointMarker(position: THREE.Vector3, color: number): THREE.Mesh {
        if (!CoordinateDimension._sharedPointGeometry) {
            CoordinateDimension._sharedPointGeometry = new THREE.SphereGeometry(0.15, 12, 12);
        }

        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            toneMapped: false
        });

        const mesh = new THREE.Mesh(CoordinateDimension._sharedPointGeometry, material);
        mesh.position.copy(position);
        mesh.renderOrder = Infinity;
        mesh.frustumCulled = false;

        return mesh;
    }

    /**
     * Çizgi oluştur
     */
    private createLine(
        positions: number[],
        color: number,
        lineWidth: number,
        opacity: number = 1
    ): LineSegments2 {
        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity,
            fog: false,
            worldUnits: false,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        const line = new LineSegments2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Çoklu satır label oluştur (X, Y, Z değerleri)
     */
    private createMultiLineLabel(
        position: THREE.Vector3,
        options: CoordinateDimensionOptions
    ): THREE.Sprite {
        const fontSize = options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;
        const precision = options.precision || DIMENSION_CONSTANTS.DEFAULT_PRECISION;
        const unit = options.unit || 'mm';
        const showUnit = options.showUnit !== false;

        const axisColors = options.axisColors || {
            x: '#E53935',  // Kırmızı
            y: '#43A047',  // Yeşil
            z: '#1E88E5'   // Mavi
        };

        // Format
        const formatCoord = (label: string, value: number): string => {
            const formatted = value.toFixed(precision);
            return showUnit ? `${label}: ${formatted} ${unit}` : `${label}: ${formatted}`;
        };

        const lines = [
            { text: formatCoord('X', this.localPoint.x), color: axisColors.x || '#E53935' },
            { text: formatCoord('Y', this.localPoint.y), color: axisColors.y || '#43A047' },
            { text: formatCoord('Z', this.localPoint.z), color: axisColors.z || '#1E88E5' }
        ];

        // Compact format
        if (options.format === 'compact') {
            const x = this.localPoint.x.toFixed(precision);
            const y = this.localPoint.y.toFixed(precision);
            const z = this.localPoint.z.toFixed(precision);
            lines.length = 0;
            lines.push({ text: `(${x}, ${y}, ${z})`, color: '#000000' });
        }

        // Canvas oluştur
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        const lineHeight = fontSize * 5;
        const padding = 20;

        // En geniş satırı bul
        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        let maxWidth = 0;
        for (const line of lines) {
            const metrics = ctx.measureText(line.text);
            maxWidth = Math.max(maxWidth, metrics.width);
        }

        canvas.width = maxWidth + padding * 2;
        canvas.height = lineHeight * lines.length + padding * 2;

        // Arka plan (hafif transparan beyaz)
        ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
        ctx.roundRect(4, 4, canvas.width - 8, canvas.height - 8, 6);
        ctx.fill();

        // Kenarlık
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.2)';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Metin çiz
        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';

        lines.forEach((line, index) => {
            const y = padding + lineHeight * (index + 0.5);

            // Text shadow
            ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;

            ctx.fillStyle = line.color;
            ctx.fillText(line.text, padding, y);
        });

        // Texture
        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        sprite.scale.set(canvas.width * 0.012, canvas.height * 0.012, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        // Original scale'i sakla (sabit boyut için)
        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Görünümü güncelle
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        if (this.leaderLine) {
            (this.leaderLine.material as LineMaterial).color.setHex(color);
        }

        if (this.pointMarker) {
            (this.pointMarker.material as THREE.MeshBasicMaterial).color.setHex(color);
        }
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        if (this.leaderLine) {
            this.remove(this.leaderLine);
            this.leaderLine.geometry.dispose();
            (this.leaderLine.material as LineMaterial).dispose();
        }

        if (this.pointMarker) {
            this.remove(this.pointMarker);
            // Shared geometry kullanıldığı için dispose etme
            (this.pointMarker.material as THREE.Material).dispose();
        }

        if (this.label) {
            this.remove(this.label);
            this.label.material.map?.dispose();
            this.label.material.dispose();
        }

        this.leaderLine = null;
        this.pointMarker = null;
        this.label = null;
    }

    // ====================================================================
    // PUBLIC API
    // ====================================================================

    /**
     * World koordinatlarını al
     */
    getWorldCoordinates(): THREE.Vector3 {
        return this.worldPoint.clone();
    }

    /**
     * Lokal koordinatlarını al
     */
    getLocalCoordinates(): THREE.Vector3 {
        return this.localPoint.clone();
    }

    /**
     * Referans frame'i değiştir
     */
    setReferenceFrame(frame: CoordinateFrame | null): void {
        this.referenceFrame = frame;
        this.localPoint.copy(this.transformToLocalFrame(this.worldPoint));
        this.buildGeometry();
    }

    /**
     * Formatlanmış koordinat string'i al
     */
    getFormattedCoordinates(): string {
        const precision = this._options.precision || 2;
        const unit = this._options.unit || 'mm';
        const showUnit = this._options.showUnit !== false;

        const x = this.localPoint.x.toFixed(precision);
        const y = this.localPoint.y.toFixed(precision);
        const z = this.localPoint.z.toFixed(precision);

        if (showUnit) {
            return `X: ${x} ${unit}\nY: ${y} ${unit}\nZ: ${z} ${unit}`;
        }
        return `X: ${x}\nY: ${y}\nZ: ${z}`;
    }

    /**
     * Dispose - 🎯 FikoOlcumCizgisi ile uyumlu kaynak temizleme
     */
    override dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;

        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }
}
